import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, Loader2, Search, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Photo recommendations → real thumbnails.
 *
 * Renders one row of stock-photo tiles per `photo_suggestions` concept
 * (queried from the keywords the writer LLM already put in its search
 * links), plus a free-text search box for the editor's own query.
 * Clicking "Use as featured" on a tile copies the full-size rendition
 * into object storage server-side and promotes it to the featured
 * image — the same path as the source-image import — and sets the
 * caption to the photographer credit.
 *
 * Providers: Pexels and Unsplash (tabs appear only for keys that are
 * configured on the backend). The payload is provider-neutral so Adobe
 * Stock can drop in later without touching this component beyond the
 * "licensing" step that will sit in front of the import. For Unsplash
 * the import also sends `download_location` so the backend can fire
 * the download event their guidelines require.
 *
 * Mounted inside a CollapsibleSection, so nothing is fetched until the
 * editor opens the section.
 *
 * Newsdesk Stock (the editors' own library, 2026-09-29) is a third
 * provider tab whenever R2 is configured. Its tiles carry `stock_id`,
 * which the import endpoints take instead of a URL.
 *
 * Reused by the body-image modal's Stock tab: pass `onPick(photo)` (a
 * promise) plus `actionLabel` / `successMessage`, and the tile button
 * inserts the photo there instead of making it the featured image.
 */
export const STOCK_PROVIDER_LABELS = {
  pexels: "Pexels",
  unsplash: "Unsplash",
  newsdesk: "Newsdesk",
};

export default function StockPhotoPicker({
  article,
  onPick,
  onPicked,
  actionLabel = "Use as featured",
  actionTitle = "Save this photo as the featured image (credit is added to the caption)",
  successMessage = "Stock photo saved as featured image",
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  // "" → backend picks the first configured provider.
  const [provider, setProvider] = useState("");

  const photosQuery = useQuery({
    queryKey: ["stock-photos", article.id, provider, query],
    queryFn: () =>
      api.getStockPhotos(article.id, {
        q: query || undefined,
        provider: provider || undefined,
      }),
    // Pexels allows 200 req/h and Unsplash only 50/h in demo mode —
    // don't refetch on every focus.
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const importMutation = useMutation({
    mutationFn: (photo) =>
      onPick
        ? onPick(photo)
        : api.importStockPhoto(article.id, {
            provider: photo.provider,
            url: photo.download_url,
            credit: photo.credit,
            download_location: photo.download_location || "",
            stock_id: photo.stock_id || "",
          }),
    onSuccess: (data, photo) => {
      toast.success(successMessage);
      qc.invalidateQueries({ queryKey: ["article", article.id] });
      if (photo.provider === "newsdesk") {
        qc.invalidateQueries({ queryKey: ["stock-library"] });
      }
      onPicked?.(data, photo);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || e?.message || "Import failed"),
  });

  const submitSearch = (e) => {
    e.preventDefault();
    setQuery(draft.trim());
  };
  const clearSearch = () => {
    setDraft("");
    setQuery("");
  };

  const groups = photosQuery.data?.groups || [];
  const activeProvider = photosQuery.data?.provider || provider || "pexels";
  const providers = photosQuery.data?.providers || [];
  const labelFor = (p) => STOCK_PROVIDER_LABELS[p] || p;
  const photoKey = (p) => `${p.provider}-${p.id}`;
  const configError =
    photosQuery.error?.response?.status === 503
      ? photosQuery.error?.response?.data?.detail
      : null;

  return (
    <div className="space-y-4" data-testid="stock-photo-picker">
      {/* Provider tabs — only shown when more than one key is configured */}
      {providers.length > 1 && (
        <div className="flex gap-1 text-xs" role="tablist">
          {providers.map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={activeProvider === p}
              data-testid={`stock-photo-provider-${p}`}
              onClick={() => setProvider(p)}
              className={
                "px-3 py-1 border " +
                (activeProvider === p
                  ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                  : "border-[var(--border)] hover:border-[var(--ink)]")
              }
            >
              {labelFor(p)}
            </button>
          ))}
        </div>
      )}

      {/* Editor search */}
      <form onSubmit={submitSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
          <input
            data-testid="stock-photo-search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search stock photos… (e.g. marina sunset Baja)"
            className="w-full text-sm pl-8 pr-8 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          {(draft || query) && (
            <button
              type="button"
              onClick={clearSearch}
              title="Back to article suggestions"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={!draft.trim() || photosQuery.isFetching}
          className="text-xs px-3 py-1.5 border border-[var(--ink)] bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-40"
        >
          Search
        </button>
      </form>

      {/* States */}
      {photosQuery.isLoading && (
        <div className="flex items-center gap-2 text-xs text-[var(--ink-muted)]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading thumbnails…
        </div>
      )}
      {configError && (
        <div className="text-xs text-[var(--ink-muted)] italic">{configError}</div>
      )}
      {photosQuery.isError && !configError && (
        <div className="text-xs text-red-700">
          {photosQuery.error?.response?.data?.detail ||
            photosQuery.error?.message ||
            "Could not load stock photos"}
        </div>
      )}
      {photosQuery.isSuccess && groups.length === 0 && (
        <div className="text-xs text-[var(--ink-muted)] italic">
          No photo suggestions for this article yet — try a search above.
        </div>
      )}

      {/* One row per concept (or the single editor search) */}
      {groups.map((g, gi) => (
        <div key={`${g.query}-${gi}`} data-testid={`stock-photo-group-${gi}`}>
          <div className="flex items-baseline justify-between gap-3 mb-1.5">
            <div className="font-serif-display text-base truncate">{g.concept}</div>
            <div className="text-[10px] text-[var(--ink-muted)] shrink-0">
              {g.query !== g.concept && <span className="mr-2">“{g.query}”</span>}
              via {labelFor(activeProvider)}
            </div>
          </div>
          {g.error ? (
            <div className="text-xs text-red-700">{g.error}</div>
          ) : g.photos.length === 0 ? (
            <div className="text-xs text-[var(--ink-muted)] italic">
              {activeProvider === "newsdesk"
                ? "No Newsdesk Stock photos match — try other keywords, or add photos in the Stock section."
                : "No results — try different keywords."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {g.photos.map((p) => {
                const pending =
                  importMutation.isPending &&
                  importMutation.variables &&
                  photoKey(importMutation.variables) === photoKey(p);
                return (
                  <div
                    key={photoKey(p)}
                    className="relative group aspect-video bg-[var(--secondary)] overflow-hidden border border-[var(--border)] hover:border-[var(--ink)]"
                  >
                    <a
                      href={p.page_url || p.preview_url}
                      target="_blank"
                      rel="noreferrer"
                      title={p.alt || p.credit}
                      className="block w-full h-full"
                    >
                      <img
                        src={p.thumb_url}
                        alt={p.alt || g.concept}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </a>
                    {/* Photographer credit — always visible so editors can
                        judge attribution before adopting. */}
                    <a
                      href={p.photographer_url || p.page_url || undefined}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute inset-x-0 top-0 px-1.5 py-0.5 text-[10px] text-white bg-black/45 truncate flex items-center gap-1 hover:bg-black/65"
                    >
                      {p.photographer || labelFor(p.provider)}
                      {(p.photographer_url || p.page_url) && (
                        <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                      )}
                    </a>
                    <button
                      data-testid={`stock-photo-use-${gi}-${p.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        importMutation.mutate(p);
                      }}
                      disabled={importMutation.isPending}
                      title={actionTitle}
                      className="absolute inset-x-1 bottom-1 text-[10px] px-2 py-1 bg-white/95 border border-[var(--border)] shadow-sm opacity-0 group-hover:opacity-100 hover:bg-white disabled:opacity-40 flex items-center justify-center gap-1"
                    >
                      {pending ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                        </>
                      ) : (
                        actionLabel
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}